import { UseQueryResult } from '@tanstack/react-query';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import {
  saveStateToStorage,
  tryRestoreStateFromStorage
} from 'lib/utils/local-storage';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useGetClinicInfoMultiple } from './tanstack/use-getClinicInfo';
import { useGetCurrentClinicianInfo } from './tanstack/use-getCurrentClinicianInfo';

interface ClinicianContextState
  extends Pick<UseQueryResult, 'isLoading' | 'isFetching' | 'isFetched'> {
  /**
   * The ID of the current clinic
   */
  currentClinic: string;

  clinicIds: string[];
  /**
   * The names of the clinics
   */
  clinicNames: string[];
  errors: Error[];
  hasErrors: boolean;

  refetch: () => void;
  setCurrentClinic: (clinicId: string) => void;

  /**
   * The ID of the current organisation
   */
  orgId?: string;
}

// Initialising the context as `null` so that if the context is missing, components that rely on the context can throw an error
const _ClinicianContext = createContext<ClinicianContextState>(null as any);

export const ClinicianContext = _ClinicianContext.Provider;

const cacheKey = (clinicianId: string) => [
  'localCache',
  clinicianId,
  'currentClinicId'
];

/**
 * Collect data to form a Clinician context state
 * @param clinicianId
 * @returns
 */
export function useCreateClinicianContextState(
  clinicianId: string
): ClinicianContextState {
  const clinicianInfo = useGetCurrentClinicianInfo(clinicianId);
  
  const [currentClinic, _setCurrentClinic] = useState(
    tryRestoreStateFromStorage(cacheKey(clinicianId), '')
  );

  const setCurrentClinic = useCallback(
    (clinicId: string) => {
      if (!clinicianInfo.data?.clinics.includes(clinicId)) {
        throw new Error(
          `Clinician ${clinicianInfo.data?.id} is not a member of clinic (id=${clinicId})`
        );
      }
      _setCurrentClinic(clinicId);
    },
    [clinicianInfo.data]
  );

  /**
   * Fetch the data from all Clinics
   */
  const clinicInfoQueries = useGetClinicInfoMultiple(
    clinicianInfo.data?.clinics ?? []
  );

  /**
   * Initialise the currentclinic value to the first clinic
   */
  useEffect(() => {
    saveStateToStorage(cacheKey(clinicianId), currentClinic);
  }, [currentClinic, clinicianId]);

  useEffect(() => {
    // If the current clinic is not set, and the clinician data has loaded, set the current clinic
    if (clinicianInfo.data && clinicianInfo.data.clinics.length > 0) {
      /**
       * Case 1: The current clinic is not set
       * Case 2: The current clinic is set, but the fetched data does not show that the current user is associated with the clinic (e.g: re-fetch, or switching between users during development)
       */
      if (
        currentClinic === '' ||
        !clinicianInfo.data.clinics.includes(currentClinic)
      ) {
        setCurrentClinic(clinicianInfo.data.clinics[0]);
        return;
      }

      return;
    }
  }, [clinicianInfo.data, currentClinic, setCurrentClinic]);

  const clinics = clinicianInfo.data?.clinics ?? [];
  const clinicInfoErrors = JSON.stringify(
    clinicInfoQueries.map(_ => _.errorUpdatedAt)
  );

  const errors = useMemo(
    () =>
      [...clinicInfoQueries, clinicianInfo]
        .filter(_ => _.isError)
        .map(_ => _.error)
        .map(_ => resolveError(_)),
    // eslint-disable-next-line
    [clinicInfoErrors, clinicianInfo.isError]
  );
  const allQueries = [clinicianInfo, ...clinicInfoQueries];
  return {
    clinicIds: clinics,
    clinicNames: clinicInfoQueries
      .filter(_ => _.data !== undefined)
      .map(_ => _.data?.name ?? ''),
    currentClinic,

    errors,
    hasErrors: errors.length !== 0,

    isFetched: allQueries.every(_ => _.isFetched),
    isFetching: allQueries.some(_ => _.isFetching),
    isLoading: allQueries.some(_ => _.isLoading),

    // TODO: Also refetch the clinic queries
    refetch: clinicianInfo.refetch,
    setCurrentClinic,
    orgId: undefined
  };
}

export function useClinicianContext() {
  const context = useContext(_ClinicianContext);
  if (context === null) {
    throw new Error(
      `useClinicianContext must be used within a ClinicianContext`
    );
  }
  return context;
}
